/**
 * Utilities for reminder "fires today" logic, mirroring backend ReminderSchedulerService.
 * Uses the reminder's timezone so "today" matches when the backend would trigger it.
 */

const RECURRENCE_DAILY = 'daily';
const RECURRENCE_WEEKLY = 'weekly';
const RECURRENCE_MONTHLY = 'monthly';
const RECURRENCE_ONCE = 'once';

/**
 * Get the browser's timezone (e.g. "America/New_York") for fallback when reminder has none.
 */
function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/**
 * Get "today" in the given timezone as { dayOfWeek: "MON"|"TUE"|..., dayOfMonth: 1-31 }.
 */
function getTodayInTimezone(timezone) {
  const tz = timezone && timezone.trim() ? timezone.trim() : getBrowserTimezone();
  const now = new Date();
  const weekdayShort = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(now);
  const dayOfWeek = weekdayShort.toUpperCase().slice(0, 3); // "Mon" -> "MON"
  const dayOfMonthStr = new Intl.DateTimeFormat('en-US', { timeZone: tz, day: 'numeric' }).format(now);
  const dayOfMonth = parseInt(dayOfMonthStr, 10) || 1;
  return { dayOfWeek, dayOfMonth };
}

/**
 * Get today's date as "yyyy-MM-dd" in the given timezone (for completedDates comparison).
 */
export function getTodayDateStringInTimezone(timezone) {
  const tz = timezone && timezone.trim() ? timezone.trim() : getBrowserTimezone();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;
  if (year && month && day) {
    return `${year}-${month}-${day}`;
  }
  const fallback = new Date().toLocaleDateString('en-CA', { timeZone: tz });
  const match = String(fallback).match(/(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : fallback;
}

export function toReminderApiDateParam(dateStr) {
  if (dateStr == null || dateStr === '') return undefined;
  const match = String(dateStr).trim().match(/(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : undefined;
}

export function resolveReminderApiId(reminderOrId) {
  if (reminderOrId == null) return '';
  if (typeof reminderOrId === 'string' || typeof reminderOrId === 'number') {
    const id = String(reminderOrId).trim();
    return (!id || id === 'undefined' || id === 'null' || id === '[object Object]') ? '' : id;
  }
  const raw = reminderOrId.reminderId ?? reminderOrId.id ?? reminderOrId._id;
  if (raw && typeof raw === 'object') {
    const nested = raw.$oid || raw.oid || raw.id || raw._id;
    return resolveReminderApiId(nested);
  }
  return resolveReminderApiId(raw);
}

/**
 * Returns true if the reminder fires on today's date in the reminder's timezone.
 * Only enabled reminders can "fire today"; disabled ones return false.
 * Mirrors backend ReminderSchedulerService.firesOnDate.
 *
 * @param {Object} reminder - { enabled, timezone, recurrence: { type, daysOfWeek, daysOfMonth } }
 * @returns {boolean}
 */
export function reminderFiresToday(reminder) {
  if (!reminder || reminder.enabled !== true) return false;

  const rec = reminder.recurrence;
  if (!rec || !rec.type || !rec.time) return false;

  const tz = reminder.timezone && reminder.timezone.trim() ? reminder.timezone.trim() : getBrowserTimezone();
  const { dayOfWeek, dayOfMonth } = getTodayInTimezone(tz);

  const type = rec.type;
  if (type === RECURRENCE_DAILY) {
    // fall through to completedDates check
  } else if (type === RECURRENCE_WEEKLY) {
    const days = rec.daysOfWeek;
    if (!Array.isArray(days) || days.length === 0) return false;
    if (!days.some((d) => d && String(d).toUpperCase().slice(0, 3) === dayOfWeek)) return false;
  } else if (type === RECURRENCE_MONTHLY) {
    const days = rec.daysOfMonth;
    if (!Array.isArray(days) || days.length === 0) return false;
    if (!days.some((d) => Number(d) === dayOfMonth)) return false;
  } else if (type === RECURRENCE_ONCE) {
    const todayStr = getTodayDateStringInTimezone(tz);
    if (!rec.date || rec.date !== todayStr) return false;
  } else {
    return false;
  }

  const completedDates = reminder.completedDates;
  if (Array.isArray(completedDates) && completedDates.length > 0) {
    const todayStr = getTodayDateStringInTimezone(tz);
    if (completedDates.includes(todayStr)) return false;
  }
  const skippedDates = reminder.skippedDates;
  if (Array.isArray(skippedDates) && skippedDates.length > 0) {
    const todayStr = getTodayDateStringInTimezone(tz);
    if (skippedDates.includes(todayStr)) return false;
  }
  return true;
}

/**
 * Get today's trigger time as minutes-since-midnight in the reminder's timezone, for sorting.
 * Only meaningful when reminderFiresToday(reminder) is true.
 * Returns 0 if recurrence/time is invalid (sorts first) or 24*60 (sorts last).
 *
 * @param {Object} reminder - { timezone, recurrence: { time } }
 * @returns {number} - minutes since midnight in reminder's TZ (0..1440)
 */
function parseHhMm(time) {
  if (!time) return null;
  const [hours, minutes] = String(time).split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return { hours, minutes };
}

export function normalizeClockTime(time) {
  const parsed = parseHhMm(time);
  if (!parsed) return '';
  return `${String(parsed.hours).padStart(2, '0')}:${String(parsed.minutes).padStart(2, '0')}`;
}

export function reminderClockTimeKey(reminderOrPayload) {
  return normalizeClockTime(
    reminderOrPayload?.time
    || reminderOrPayload?.recurrence?.time
  );
}

export function getTriggerTimeToday(reminder) {
  const parsed = parseHhMm(reminder?.recurrence?.time);
  if (!parsed) return 24 * 60;
  const minutesSinceMidnight = (parsed.hours || 0) * 60 + (parsed.minutes || 0);
  return Math.min(24 * 60 - 1, Math.max(0, minutesSinceMidnight));
}

/** Clock label like "2:30 PM". */
export function formatClockTime(timeHhMm) {
  const parsed = parseHhMm(timeHhMm);
  if (!parsed) return '';
  const period = parsed.hours >= 12 ? 'PM' : 'AM';
  const hour12 = parsed.hours % 12 || 12;
  return `${hour12}:${String(parsed.minutes || 0).padStart(2, '0')} ${period}`;
}

function zonedLocalToUtcMs(dateStr, timeHhMm, timezone) {
  const [year, month, day] = String(dateStr).split('-').map(Number);
  const parsed = parseHhMm(timeHhMm);
  if (!year || !month || !day || !parsed) return null;
  const tz = timezone && String(timezone).trim() ? String(timezone).trim() : getBrowserTimezone();
  const utcGuess = Date.UTC(year, month - 1, day, parsed.hours, parsed.minutes, 0);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcGuess));
  const map = Object.fromEntries(parts.filter((p) => p.type !== 'literal').map((p) => [p.type, p.value]));
  const asUtc = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour) % 24,
    Number(map.minute),
    Number(map.second),
  );
  return utcGuess - (asUtc - utcGuess);
}

function dateStringInTimezone(ms, timezone) {
  const tz = timezone && String(timezone).trim() ? String(timezone).trim() : getBrowserTimezone();
  return new Date(ms).toLocaleDateString('en-CA', { timeZone: tz });
}

function hhMmInTimezone(ms, timezone) {
  const tz = timezone && String(timezone).trim() ? String(timezone).trim() : getBrowserTimezone();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(ms));
  const hour = parts.find((p) => p.type === 'hour')?.value || '00';
  const minute = parts.find((p) => p.type === 'minute')?.value || '00';
  return `${String(Number(hour) % 24).padStart(2, '0')}:${minute}`;
}

export function parseSnoozeUntilMs(reminder) {
  const raw = reminder?.snoozeUntil;
  if (!raw) return null;
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) {
    return zonedLocalToUtcMs(raw.slice(0, 10), raw.slice(11, 16), reminder.timezone);
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}

export function getReminderTriggerMs(reminder, dateStr) {
  if (!reminder || !dateStr) return null;
  const scheduledMs = zonedLocalToUtcMs(dateStr, reminder.recurrence?.time, reminder.timezone);
  const snoozeMs = parseSnoozeUntilMs(reminder);
  if (snoozeMs && (!scheduledMs || snoozeMs >= scheduledMs)) return snoozeMs;
  return scheduledMs;
}

/** Minutes-from-midnight (and extra days) so lists stay ordered by next trigger. */
export function getSortableTriggerMinutes(reminder, nowMs = Date.now()) {
  const snoozeMs = parseSnoozeUntilMs(reminder);
  if (snoozeMs && snoozeMs > nowMs) {
    const tz = reminder.timezone;
    const todayStr = getTodayDateStringInTimezone(tz);
    const snoozeDate = dateStringInTimezone(snoozeMs, tz);
    const hhmm = hhMmInTimezone(snoozeMs, tz);
    const parsed = parseHhMm(hhmm);
    const minutes = parsed ? parsed.hours * 60 + parsed.minutes : 0;
    if (snoozeDate > todayStr) {
      const dayDelta = Math.max(1, Math.round((Date.parse(`${snoozeDate}T00:00:00Z`) - Date.parse(`${todayStr}T00:00:00Z`)) / 86400000));
      return dayDelta * 24 * 60 + minutes;
    }
    return minutes;
  }
  return getTriggerTimeToday(reminder);
}

export function isReminderTriggerOverdue(reminder, dateStr, todayStr, nowMs = Date.now()) {
  if (!reminder || !dateStr) return false;
  if (reminderCompletedOnDate(reminder, dateStr)) return false;
  if (reminderSkippedOnDate(reminder, dateStr)) return false;
  const snoozeMs = parseSnoozeUntilMs(reminder);
  if (snoozeMs && snoozeMs > nowMs) return false;
  if (todayStr && dateStr > todayStr) return false;
  if (todayStr && dateStr === todayStr && !reminderIsScheduledOnDate(reminder, dateStr) && !snoozeMs) {
    return false;
  }
  const triggerMs = getReminderTriggerMs(reminder, dateStr);
  return triggerMs != null && triggerMs < nowMs;
}

export function getReminderTriggerDisplay(reminder, dateStr, todayStr, nowMs = Date.now()) {
  const snoozeMs = parseSnoozeUntilMs(reminder);
  const snoozed = Boolean(snoozeMs && snoozeMs > nowMs);
  const clockSource = snoozed
    ? hhMmInTimezone(snoozeMs, reminder.timezone)
    : reminder?.recurrence?.time;
  return {
    clockLabel: formatClockTime(clockSource),
    overdue: isReminderTriggerOverdue(reminder, dateStr, todayStr, nowMs),
    snoozed,
  };
}

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

export function shiftDateString(dateStr, deltaDays) {
  const [year, month, day] = String(dateStr).split('-').map(Number);
  if (!year || !month || !day) return dateStr;
  const utc = new Date(Date.UTC(year, month - 1, day + deltaDays));
  return utc.toISOString().slice(0, 10);
}

function weekdayOfDateString(dateStr) {
  const [year, month, day] = String(dateStr).split('-').map(Number);
  if (!year || !month || !day) return '';
  return WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
}

function dayOfMonthOfDateString(dateStr) {
  return parseInt(String(dateStr).slice(8, 10), 10) || 0;
}

/** True if the reminder is scheduled on this calendar date (ignores completedDates). */
export function reminderIsScheduledOnDate(reminder, dateStr) {
  const rec = reminder?.recurrence;
  if (!rec?.type || !dateStr) return false;
  const type = rec.type;
  if (type === RECURRENCE_DAILY) return true;
  if (type === RECURRENCE_WEEKLY) {
    const days = rec.daysOfWeek;
    if (!Array.isArray(days) || days.length === 0) return false;
    const weekday = weekdayOfDateString(dateStr);
    return days.some((d) => d && String(d).toUpperCase().slice(0, 3) === weekday);
  }
  if (type === RECURRENCE_MONTHLY) {
    const days = rec.daysOfMonth;
    if (!Array.isArray(days) || days.length === 0) return false;
    const day = dayOfMonthOfDateString(dateStr);
    return days.some((d) => Number(d) === day);
  }
  if (type === RECURRENCE_ONCE) {
    return rec.date === dateStr;
  }
  return false;
}

export function formatReminderScheduleSummary(reminder) {
  const clock = formatClockTime(reminder?.recurrence?.time);
  const type = reminder?.recurrence?.type;
  let cadence = '';
  if (type === RECURRENCE_DAILY) cadence = 'Daily';
  else if (type === RECURRENCE_WEEKLY) {
    const days = Array.isArray(reminder.recurrence?.daysOfWeek)
      ? reminder.recurrence.daysOfWeek.filter(Boolean).join(', ')
      : '';
    cadence = days ? `Weekly · ${days}` : 'Weekly';
  } else if (type === RECURRENCE_MONTHLY) cadence = 'Monthly';
  else if (type === RECURRENCE_ONCE) {
    cadence = reminder.recurrence?.date ? `Once · ${reminder.recurrence.date}` : 'Once';
  }
  if (clock && cadence) return `Triggers at ${clock} · ${cadence}`;
  if (clock) return `Triggers at ${clock}`;
  return cadence;
}

/**
 * Scheduled occurrences newest first. Pass `limit`, and/or `fromDate`/`toDate` (yyyy-MM-dd).
 */
export function getLastOccurrenceHistory(reminder, limitOrOptions = 5) {
  if (!reminder) return [];
  const options = typeof limitOrOptions === 'number'
    ? { limit: limitOrOptions }
    : (limitOrOptions || {});
  const limit = options.limit;
  const fromDate = options.fromDate || null;
  const tz = reminder.timezone;
  const todayStr = getTodayDateStringInTimezone(tz);
  let toDate = options.toDate || todayStr;
  if (toDate > todayStr) toDate = todayStr;
  const completed = new Set(
    Array.isArray(reminder.completedDates) ? reminder.completedDates.filter(Boolean) : [],
  );
  const skipped = new Set(
    Array.isArray(reminder.skippedDates) ? reminder.skippedDates.filter(Boolean) : [],
  );
  let createdStr = null;
  if (reminder.createdAt) {
    const created = new Date(reminder.createdAt);
    if (!Number.isNaN(created.getTime())) {
      createdStr = created.toLocaleDateString('en-CA', {
        timeZone: tz && String(tz).trim() ? tz.trim() : getBrowserTimezone(),
      });
    }
  }

  const results = [];
  let cursor = toDate;
  let guard = 0;
  const maxScan = fromDate ? 800 : 400;
  while (guard < maxScan) {
    guard += 1;
    if (createdStr && cursor < createdStr) break;
    if (fromDate && cursor < fromDate) break;
    if (!fromDate && limit && results.length >= limit) break;
    if (reminderIsScheduledOnDate(reminder, cursor)) {
      results.push({
        date: cursor,
        completed: completed.has(cursor),
        skipped: skipped.has(cursor),
        isToday: cursor === todayStr,
        taskProgress: reminderTaskProgress(reminder, cursor),
      });
    }
    cursor = shiftDateString(cursor, -1);
  }
  return results;
}

function dayOrdinal(day) {
  const v = day % 100;
  if (v >= 11 && v <= 13) return `${day}th`;
  switch (day % 10) {
    case 1: return `${day}st`;
    case 2: return `${day}nd`;
    case 3: return `${day}rd`;
    default: return `${day}th`;
  }
}

/** { day: "23rd", month: "DEC" } for last-5 history chips. */
export function formatOccurrenceParts(dateStr) {
  const [year, month, day] = String(dateStr).split('-').map(Number);
  if (!year || !month || !day) return { day: dateStr, month: '' };
  const utc = new Date(Date.UTC(year, month - 1, day));
  return {
    day: dayOrdinal(day),
    month: utc.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase(),
  };
}

export function reminderTasks(reminder) {
  if (!Array.isArray(reminder?.tasks)) return [];
  return reminder.tasks.filter((task) => task && (task.id || String(task.title || '').trim()));
}

export function completedTaskIdsForDate(reminder, dateStr) {
  if (!dateStr || !Array.isArray(reminder?.taskDays)) return [];
  const day = reminder.taskDays.find((item) => item && item.date === dateStr);
  return Array.isArray(day?.completedTaskIds) ? day.completedTaskIds.filter(Boolean) : [];
}

export function reminderTaskProgress(reminder, dateStr) {
  const tasks = reminderTasks(reminder);
  const total = tasks.length;
  if (!total) return null;
  const doneIds = new Set(completedTaskIdsForDate(reminder, dateStr));
  const done = tasks.filter((task) => task.id && doneIds.has(task.id)).length;
  return {
    done,
    total,
    percent: Math.round((done / total) * 100),
  };
}

export function reminderCompletedOnDate(reminder, dateStr) {
  return Boolean(dateStr) && Array.isArray(reminder?.completedDates) && reminder.completedDates.includes(dateStr);
}

export function reminderSkippedOnDate(reminder, dateStr) {
  return Boolean(dateStr) && Array.isArray(reminder?.skippedDates) && reminder.skippedDates.includes(dateStr);
}

/** Pending on a given calendar day. Today still requires the reminder to be enabled. */
export function reminderPendingOnDate(reminder, dateStr, todayStr) {
  if (!reminder || !dateStr) return false;
  if (reminderCompletedOnDate(reminder, dateStr)) return false;
  if (reminderSkippedOnDate(reminder, dateStr)) return false;
  if (dateStr === todayStr) return reminderFiresToday(reminder);
  return reminderIsScheduledOnDate(reminder, dateStr);
}

export function listRecentDates(todayStr, count = 14) {
  const days = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    days.push(shiftDateString(todayStr, -i));
  }
  return days;
}

export function formatDateBarParts(dateStr) {
  const [year, month, day] = String(dateStr).split('-').map(Number);
  if (!year || !month || !day) return { weekday: '', dayNum: dateStr };
  const utc = new Date(Date.UTC(year, month - 1, day));
  return {
    weekday: utc.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }),
    dayNum: String(day),
  };
}

export function formatSelectedDayLabel(dateStr, _todayStr) {
  const [year, month, day] = String(dateStr).split('-').map(Number);
  if (!year || !month || !day) return dateStr;
  const utc = new Date(Date.UTC(year, month - 1, day));
  return utc.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export function formatOccurrenceDateLabel(dateStr, isToday = false) {
  if (isToday) return 'Today';
  const parts = formatOccurrenceParts(dateStr);
  return parts.month ? `${parts.day} ${parts.month}` : dateStr;
}
